import { useMemo, useRef } from 'react'
import type { Section, Task } from '../../../lib/db/models'
import type { NestMenu } from '../utils/nestMenu'

interface Segment { section: Section | null; tasks: Task[] }

/**
 * Baut **„Einhängen in …“** für das Aufgaben-Menü (#204) aus der Liste einer Seite. Stabil
 * über Refs: Das Menü liest erst beim Öffnen, und ein neuer Wert bei jeder Auswahl ließe jedes
 * Menü jeder Zeile neu rendern.
 *
 * @param nest hängt Aufgaben ein (`useNestActions().nest`)
 */
export function useNestMenu({ segments, selectedIds, nest, untitled, selectInList = () => {} }: {
  segments: Segment[]
  selectedIds: ReadonlySet<string>
  nest: (ids: string[], parentId: string) => unknown
  untitled: string
  /** Wählt Aufgaben in der Liste aus, sobald sie dort stehen. */
  selectInList?: (ids: string[]) => void
}): NestMenu {
  const latest = useRef({ segments, selectedIds, nest, untitled, selectInList })
  latest.current = { segments, selectedIds, nest, untitled, selectInList }

  return useMemo<NestMenu>(() => {
    /** Die Auswahl, wenn die Aufgabe dazugehört — sonst die Aufgabe allein; in Listenreihenfolge. */
    const idsFor = (taskId: string, ids?: readonly string[]) => {
      if (ids) return [...ids]
      const { segments: segs, selectedIds: picked } = latest.current
      if (!picked.has(taskId)) return [taskId]
      return segs.flatMap(seg => seg.tasks).filter(task => picked.has(task.id)).map(task => task.id)
    }
    return {
      groupsFor(taskId, ids) {
        const moving = new Set(idsFor(taskId, ids))
        const tasks = latest.current.segments.flatMap(seg => seg.tasks)
        // Wiederkehrend in wiederkehrend gibt es nicht (#205).
        const recurring = tasks.some(task => moving.has(task.id) && task.recurrence != null)
        return latest.current.segments.map((seg, i) => ({
          id: seg.section?.id ?? `none-${i}`,
          title: seg.section?.title ?? null,
          options: seg.tasks.map(task => ({
            id: task.id,
            title: task.title || latest.current.untitled,
            disabled: moving.has(task.id) || task.status === 'completed' || (recurring && task.recurrence != null),
          })),
        }))
      },
      nest(taskId, parentId, ids) {
        void latest.current.nest(idsFor(taskId, ids), parentId)
      },
      selectInList(ids) {
        latest.current.selectInList(ids)
      },
      moveSubtasks(ids, parentId) {
        void latest.current.nest(ids, parentId)
      },
    }
  }, [])
}
